/** @format */

import * as Yup from 'yup';
import { useState } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Button,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// component
import Iconify from '../../components/Iconify';
import User from "../../dao/User";
import Swal from '../../reusables/CustomSwal';
import { useDispatch } from 'react-redux';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [loginStep, setLoginStep] = useState(1); // 1: email/password, 2: TOTP verify, 3: TOTP setup
  const [userCredentials, setUserCredentials] = useState({ email: '', password: '' });
  const [totpSetupData, setTotpSetupData] = useState(null); // { secret, qrCodeURL, authenticatorURL }

  const attemptInitialLogin = async (email, password) => {
    try {
      // First, try to login with TOTP endpoint to check if TOTP is required
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}v1/user/login-totp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
          // Don't send totpToken initially to check if it's required
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        
        if (error.code === 'TOTP_REQUIRED') {
          // TOTP is required, move to step 2
          console.log('TOTP required, switching to step 2');
          setUserCredentials({ email, password });
          setLoginStep(2);
          formik.setFieldValue('totpToken', '');
          formik.setSubmitting(false);
          
          await Swal.fire({
            title: 'Two-Factor Authentication Required',
            text: 'Please enter your 6-digit authentication code.',
            icon: 'info',
          });
          return;
        } else if (error.code === 'TOTP_SETUP') {
          // TOTP setup is required, move to step 3
          console.log('TOTP setup required, switching to step 3');
          setUserCredentials({ email, password });
          setTotpSetupData({
            secret: error.secret,
            qrCodeURL: error.qrCodeURL,
            authenticatorURL: error.authenticatorURL,
          });
          setLoginStep(3);
          formik.setFieldValue('totpToken', '');
          formik.setSubmitting(false);

          await Swal.fire({
            title: 'Setup Two-Factor Authentication',
            text: 'Two-factor authentication is required for your account. Please scan the QR code with your authenticator app and enter the 6-digit code.',
            icon: 'info',
          });
          return;
        } else {
          throw error;
        }
      }

      // If we get here, login was successful without TOTP
      console.log('Login successful without TOTP');
      const result = await response.json();
      
      // Store authentication data
      localStorage.token = result.token;
      localStorage.id = result.id;
      localStorage.role = result.role;
      localStorage.email = result.email;
      
      const { profile } = await User.getByID(result.id);
      const name = `${profile.first_name} ${profile.last_name}`;
      localStorage.name = name;
      
      dispatch({ type: 'AUTH_SET', auth: { ...result, name } });
      
    } catch (e) {
      let errorMessage = 'An error occurred.';
      if (e.code === 'EMAIL_NOT_FOUND' || e.code === 'USER_PASSWORD_WRONG') {
        errorMessage = 'Invalid credentials';
      }
      await Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
      });
    } finally {
      formik.setSubmitting(false);
    }
  };

  const attemptTOTPLogin = async (totpToken, setupSecret = null) => {
    try {
      const payload = {
        email: userCredentials.email,
        password: userCredentials.password,
        totpToken: totpToken,
      };

      if (setupSecret) {
        payload.secret = setupSecret;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}v1/user/login-totp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw error;
      }

      const result = await response.json();
      
      // Store authentication data
      localStorage.token = result.token;
      localStorage.id = result.id;
      localStorage.role = result.role;
      localStorage.email = result.email;
      
      const { profile } = await User.getByID(result.id);
      const name = `${profile.first_name} ${profile.last_name}`;
      localStorage.name = name;
      
      dispatch({ type: 'AUTH_SET', auth: { ...result, name } });
      
    } catch (e) {
      let errorMessage = 'Invalid authentication code. Please try again.';
      if (e.code === 'INVALID_TOTP') {
        errorMessage = 'The authentication code is incorrect or expired.';
      } else if (e.code === 'TOTP_REQUIRED' || e.code === 'TOTP_SETUP') {
        errorMessage = 'Authentication code is required.';
      }
      
      await Swal.fire({
        title: 'Authentication Failed',
        text: errorMessage,
        icon: 'error',
      });
    } finally {
      formik.setSubmitting(false);
    }
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email('Email must be a valid email address')
      .required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const TOTPSchema = Yup.object().shape({
    totpToken: Yup.string()
      .required('Authentication code is required')
      .length(6, 'Authentication code must be 6 digits'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      totpToken: '',
      remember: true,
    },
    validationSchema: loginStep === 1 ? LoginSchema : TOTPSchema,
    onSubmit: (values) => {
      if (loginStep === 1) {
        attemptInitialLogin(values.email, values.password);
      } else if (loginStep === 2) {
        if (!values.totpToken || values.totpToken.length !== 6) {
          Swal.fire({
            title: 'Invalid Code',
            text: 'Please enter a 6-digit authentication code.',
            icon: 'warning',
          });
          return;
        }
        attemptTOTPLogin(values.totpToken);
      } else if (loginStep === 3) {
        if (!values.totpToken || values.totpToken.length !== 6) {
          Swal.fire({
            title: 'Invalid Code',
            text: 'Please enter a 6-digit authentication code.',
            icon: 'warning',
          });
          return;
        }
        attemptTOTPLogin(values.totpToken, totpSetupData?.secret);
      }
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };
  const onKeyPress = (ev) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      handleSubmit();
    }
  };

  const resetToStep1 = () => {
    setLoginStep(1);
    setTotpSetupData(null);
    setUserCredentials({ email: '', password: '' });
    formik.resetForm();
    formik.setSubmitting(false);
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        {loginStep === 1 && (
          // Step 1: Email and Password
          <Stack spacing={3} sx={{ my: 2 }}>
            <TextField
              fullWidth
              autoComplete="email"
              type="email"
              label="Email address"
              {...getFieldProps('email')}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
              onKeyPress={onKeyPress}
            />

            <TextField
              fullWidth
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              {...getFieldProps('password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Iconify
                        icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
              onKeyPress={onKeyPress}
            />
          </Stack>
        )}

        {loginStep === 2 && (
          // Step 2: TOTP Authentication
          <Stack spacing={3} sx={{ my: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Iconify 
                icon="mdi:shield-key-outline" 
                width={48} 
                height={48} 
                color="primary.main"
                sx={{ mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                Two-Factor Authentication
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter the 6-digit code from your authenticator app
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Logging in as: <strong>{userCredentials.email}</strong>
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="6-digit authentication code"
              {...getFieldProps('totpToken')}
              error={Boolean(touched.totpToken && errors.totpToken)}
              helperText={touched.totpToken && errors.totpToken}
              placeholder="123456"
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '1.2em', letterSpacing: '0.2em' }
              }}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                formik.setFieldValue('totpToken', value);
              }}
              onKeyPress={onKeyPress}
            />

            <Button
              fullWidth
              variant="outlined"
              onClick={resetToStep1}
              startIcon={<Iconify icon="eva:arrow-back-outline" />}
            >
              Back
            </Button>
          </Stack>
        )}

        {loginStep === 3 && (
          // Step 3: Setup 2FA (QR Code + Manual Secret + OTP Input)
          <Stack spacing={2.5} sx={{ my: 1.5 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Iconify 
                icon="mdi:shield-key-outline" 
                width={48} 
                height={48} 
                color="primary.main"
                sx={{ mb: 1 }}
              />
              <Typography variant="h6" gutterBottom>
                Setup Two-Factor Authentication
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Scan this QR code with Google Authenticator or Authy
              </Typography>
            </Box>

            {totpSetupData?.qrCodeURL && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={totpSetupData.qrCodeURL}
                  alt="2FA QR Code"
                  sx={{
                    width: 180,
                    height: 180,
                    p: 1,
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                  }}
                />
              </Box>
            )}

            {totpSetupData?.secret && (
              <Box sx={{ bgcolor: 'background.neutral', p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Can't scan? Enter key manually:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={totpSetupData.secret}
                  InputProps={{
                    readOnly: true,
                    style: { fontFamily: 'monospace', fontSize: '0.85rem' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(totpSetupData.secret);
                            Swal.fire({
                              toast: true,
                              position: 'top-end',
                              showConfirmButton: false,
                              timer: 2000,
                              icon: 'success',
                              title: 'Key copied to clipboard',
                            });
                          }}
                          edge="end"
                          title="Copy Key"
                        >
                          <Iconify icon="eva:copy-outline" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

            <TextField
              fullWidth
              label="6-digit verification code"
              {...getFieldProps('totpToken')}
              error={Boolean(touched.totpToken && errors.totpToken)}
              helperText={touched.totpToken && errors.totpToken}
              placeholder="123456"
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '1.2em', letterSpacing: '0.2em' }
              }}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                formik.setFieldValue('totpToken', value);
              }}
              onKeyPress={onKeyPress}
            />

            <Button
              fullWidth
              variant="outlined"
              onClick={resetToStep1}
              startIcon={<Iconify icon="eva:arrow-back-outline" />}
            >
              Back to Login
            </Button>
          </Stack>
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 2 }}
        ></Stack>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          disabled={loginStep !== 1 && (!formik.values.totpToken || formik.values.totpToken.length !== 6)}
        >
          {loginStep === 1
            ? 'Login'
            : loginStep === 2
            ? 'Verify & Login'
            : 'Activate 2FA & Login'}
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
}
